import { useEffect, useRef, useState, useCallback, ElementType, useMemo } from "react";

// models
import { Employee2, EMPTY_KARYAWAN } from "../../../../../../model/Employee";

// Utils
import { getThemeStyles, Theme } from '../../../../../../util/style';

// lucide icons
import { GraduationCap, Landmark, NotepadText, Plus, Save, SquarePen, Trash2, UserRoundX, X } from 'lucide-react';

// components
import { fetchGetKota, FormField, calculateAge, makeInitials, fetchPostKaryawan, fetchPutKaryawan } from '../../../../../../controller/dashboard/Umum/Personalia/DataKaryawan';
import { FormUploadField } from "../components/form/FormUploadField";

type EmployeeFormTab = "umum" | "rekening" | "pendidikan" | "resign";
type City = {
    id: number | string;
    nama_kota: string;
};

type EmployeeForm = Omit<Employee2, "id"> & {
    id?: number;
};

const createEmployeeForm = (employee?: Employee2 | null): EmployeeForm => ({ ...EMPTY_KARYAWAN, ...(employee ?? {}), id: employee?.id });

export default function EmployeeModalForm({ theme = "light", isOpen, onClose, employee }: { theme?: Theme; isOpen: boolean; onClose: () => void; employee?: Employee2 | null; }) {
    const s = getThemeStyles(theme);
    const modalRef = useRef<HTMLDivElement>(null);

    const [activeTab, setActiveTab] = useState<EmployeeFormTab>("umum");

    type FormTab = { key: EmployeeFormTab; label: string; icon: ElementType; };

    const [resignEnabled, setResignEnabled] = useState(!!employee?.resign);

    const FORM_TABS: FormTab[] = [
        { key: "umum", label: "Informasi Umum", icon: NotepadText },
        { key: "rekening", label: "Informasi Rekening", icon: Landmark },
        { key: "pendidikan", label: "Riwayat Pendidikan", icon: GraduationCap },
        { key: "resign", label: "Informasi Resign", icon: UserRoundX },
    ];

    const navigateTab = (direction: 1 | -1) => {
        const currentIndex = FORM_TABS.findIndex((item) => item.key === activeTab);
        const targetIndex = currentIndex + direction;
        if (targetIndex < 0 || targetIndex >= FORM_TABS.length) return;
        setActiveTab(FORM_TABS[targetIndex].key);
    };

    const handleNextTab = () => navigateTab(1);

    const handlePreviousTab = () => navigateTab(-1);

    const handleEdit = async () => {
        const saved = { ...employeeForm, inisial: makeInitials(employeeForm.nama), rekening: rekening, resign: resignEnabled ? employeeForm.resign : null, nama_old : employee?.nama, nik_old : employee?.nik };
        setProcessStatus("loading");
        try {

        const result : any = await fetchPutKaryawan(saved);
        // console.log(result);
        if (checkSuccess(result)) {
            setProcessStatus("success");
            setTimeout(() => onClose(), 1500);
        } else {
            console.error("Edit failed:", result);
            setProcessStatus("error");
            setTimeout(() => setProcessStatus("idle"), 1500);
        }
        } catch (error) {
        console.error(error);
        setProcessStatus("error");
        }
    };

    const isFirstTab = activeTab === FORM_TABS[0].key;
    const isLastTab = activeTab === FORM_TABS[FORM_TABS.length - 1].key;

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => { if (!modalRef.current?.contains(event.target as Node)) { onClose(); } };
        document.addEventListener("mousedown", handleClickOutside);
        return () => { document.removeEventListener("mousedown", handleClickOutside); };
    }, [isOpen, onClose]);

    const [cities, setCities] = useState<City[]>([]);

    useEffect(() => {
        const fetchCities = async () => { try { const data = await fetchGetKota(); setCities(data); } catch { setCities([]); } };
        fetchCities();
    }, []);

    const [employeeForm, setEmployeeForm] = useState<EmployeeForm>(() => createEmployeeForm(employee));

    useEffect(() => {
        if (!isOpen) return;
        setEmployeeForm(createEmployeeForm(employee));
        setRekEntries(Object.entries(employee?.rekening ?? {}));
    }, [employee, isOpen]);

    const updateEmployeeField = useCallback(<Field extends keyof EmployeeForm>(field: Field,value: EmployeeForm[Field]) => { setEmployeeForm((prev) => ({ ...prev, [field]: value, })); }, [] );
    const updatePendidikanField = useCallback( <Field extends keyof EmployeeForm["pendidikan"]>( field: Field, value: EmployeeForm["pendidikan"][Field] ) => { setEmployeeForm((prev) => ({ ...prev, pendidikan: { ...prev.pendidikan, [field]: value, }, })); }, [] );
    const updateResignField = useCallback(<Field extends keyof NonNullable<EmployeeForm["resign"]>>( field: Field, value: NonNullable<EmployeeForm["resign"]>[Field] ) => { setEmployeeForm((prev) => { const resign = { tanggal_keluar: prev.resign?.tanggal_keluar ?? "", alasan: prev.resign?.alasan ?? "", keterangan: prev.resign?.keterangan ?? "", [field]: value, }; return { ...prev, resign }; }); }, []);

    useEffect(() => {
        if (!employeeForm.tgl_lahir) return;
        const usia = calculateAge(employeeForm.tgl_lahir);
        if (usia === employeeForm.usia) return; 
        updateEmployeeField("usia", usia);
    }, [ employeeForm.tgl_lahir, employeeForm.usia, updateEmployeeField ]);

    const [processStatus, setProcessStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const checkSuccess = (result: any) => { return Boolean( result?.ok || result?.status === 200 || result?.status === 201 || result?.status === "200" || result?.status === "201" || String(result?.status || "").startsWith("2") || result?.status === "success" ); };
    const [rekEntries, setRekEntries] = useState<[string, string][]>(() => Object.entries(employee?.rekening ?? {}));
    const rekening = useMemo<Record<string, string>>(() => rekEntries.reduce<Record<string, string>>((result, [key, value]) => {
                const accountName = key.trim();
                if (!accountName) return result;
                result[accountName] = value.trim();
                return result;
            }, {}),
        [rekEntries]
    );

    const addRekEntry = () => setRekEntries((p) => [...p, ["", ""]]);
    const updateRek = (i: number, col: 0 | 1, val: string) => { setRekEntries((p) => p.map((e, idx) => idx === i ? (col === 0 ? [val, e[1]] : [e[0], val]) : e));};
    const removeRek = (i: number) => setRekEntries((p) => p.filter((_, idx) => idx !== i));

    const handleSave = async () => {
        const payload = { ...employeeForm, inisial: makeInitials(employeeForm.nama), rekening: rekening, resign: null, nama_old: employee?.nama, nik_old: employee?.nik };
        setProcessStatus("loading");

        try { 
            const result = await fetchPostKaryawan(payload);

            if (!checkSuccess(result)) {
                console.error("Save failed:", result);
                setProcessStatus("error");

                setTimeout(() => {
                    setProcessStatus("idle");
                }, 1500);

                return;
            }

            setProcessStatus("success");

            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (error) {
            console.error(error);
            setProcessStatus("error");
        }
    };

    if (!isOpen && !employee) return null;

    return (
        <div className="modal-overlay">
            <div className={`modal ${s.card}`} ref={modalRef}>
                <div className="modal-header">
                    <span className={`modal-title ${s.text}`}>{employee ? "Edit Karyawan" : "Tambah Karyawan"}</span>
                    <button className="modal-close" onClick={() => { onClose(); setActiveTab("umum") }}><X /></button>
                </div>
                <div className="modal-tabs">
                    {FORM_TABS.map(({ key, label, icon: Icon }) => (
                        <button
                            key={key}
                            className={`modal-tab ${activeTab === key ? "active" : ""}`}
                            onClick={() => setActiveTab(key)}
                        >
                            <Icon />
                            {label}
                        </button>
                    ))}
                </div>
                <div className="modal-body">
                    {activeTab === "umum" && (
                        <div className="form-grid">
                            <FormField label="Foto Karyawan" full>
                                <FormUploadField theme={theme} value={employeeForm?.foto_karyawan} onChange={(e) => updateEmployeeField("foto_karyawan", e)} employee={employee} />
                            </FormField>

                            <div className="form-section-head" onClick={() => console.log(employeeForm)}>Data Pribadi</div>

                            <FormField label="NIK (Nomor Induk Kependudukan)">
                                <input type="text" className="form-input" value={String(employeeForm?.nik ?? "")} onChange={(e) => updateEmployeeField("nik", e.target.value)} placeholder="7171xxxxxxxxxxxx" />
                            </FormField>
                            <FormField label="Nama Lengkap">
                                <input type="text" className="form-input" value={String(employeeForm?.nama ?? "")} onChange={(e) => updateEmployeeField("nama", e.target.value)} placeholder="Budi Santoso" />
                            </FormField>
                            <FormField label="Jenis Kelamin">
                                <select className="form-select" value={employeeForm?.jk} onChange={(e) => updateEmployeeField("jk", e.target.value)}>
                                    <option value="">Pilih jenis kelamin</option>
                                    <option value="LK">Laki-laki</option>
                                    <option value="PM">Perempuan</option>
                                </select>
                            </FormField>
                            <FormField label="Agama">
                                <select className="form-select" value={employeeForm?.agama} onChange={(e) => updateEmployeeField("agama", e.target.value)}>
                                    <option value="">Pilih agama</option>
                                    {["Islam", "Kristen", "Katolik", "Hindu", "Buddha", "Konghucu"].map((v) => <option key={v} value={v}>{v}</option> )}
                                </select>
                            </FormField>
                            <FormField label="Tanggal Lahir">
                                <input type="date" className="form-input" value={employeeForm.tgl_lahir.split('T')[0]} onChange={(e) => updateEmployeeField("tgl_lahir", e.target.value)} />
                            </FormField>
                            <FormField label="Usia">
                                <input type="number" className="form-input" value={Number(employeeForm?.usia ?? "")} onChange={(e) => updateEmployeeField("usia", parseInt(e.target.value) || 0)} disabled />
                            </FormField>
                            <FormField label="Nomor HP Pribadi">
                                <input type="text" className="form-input" value={String(employeeForm.no_hp_pribadi ?? "")} onChange={(e) => updateEmployeeField("no_hp_pribadi", e.target.value)} placeholder="081234567890" />
                            </FormField>
                            <FormField label="Nomor HP Perusahaan">
                                <input type="text" className="form-input" value={String(employeeForm.no_hp_perusahaan ?? "")} onChange={(e) => updateEmployeeField("no_hp_perusahaan", e.target.value || null)} placeholder="081234567891" />
                            </FormField>
                            <FormField label="Email" full>
                                <input type="email" className="form-input" value={String(employeeForm.email ?? "")} onChange={(e) => updateEmployeeField("email", e.target.value)} placeholder="budi.santoso@perusahaan.co.id" />
                            </FormField>
                            <FormField label="Alamat Sesuai KTP" full>
                                <input className="form-input" value={String(employeeForm.alamat_rumah ?? "")} onChange={(e) => updateEmployeeField("alamat_rumah", e.target.value)} placeholder="Jl. Sam Ratulangi No. 10" />
                            </FormField>
                            <FormField label="Alamat Domisili" full>
                                <input className="form-input" value={String(employeeForm.alamat_sekarang ?? "")} onChange={(e) => updateEmployeeField("alamat_sekarang", e.target.value)} placeholder="Jl. Wolter Monginsidi No. 25" />
                            </FormField>
                            <FormField label="Kabupaten/Kota">
                                <select className="form-select" value={employeeForm.kota} onChange={(e) => updateEmployeeField("kota", e.target.value)}>
                                    <option value="">Pilih kabupaten/kota</option>
                                    {cities.map((v) => <option key={v.id} value={v.id}>{v.nama_kota}</option> )}
                                </select>
                            </FormField>
                            <FormField label="Kode Pos">
                                <input type="text" className="form-input" value={String(employeeForm.kode_pos ?? "")} onChange={(e) => updateEmployeeField("kode_pos", e.target.value)} placeholder="95111" />
                            </FormField>
                            <FormField label="NPWP (Nomor Pokok Wajib Pajak)">
                                <input type="text" className="form-input" value={String(employeeForm.npwp ?? "")} onChange={(e) => updateEmployeeField("npwp", e.target.value || null)} placeholder="12.345.678.9-012.345" />
                            </FormField>

                            <div className="form-section-head mt-2">Data Pekerjaan</div>

                            <FormField label="Jabatan">
                                <input type="text" className="form-input" value={String(employeeForm.jabatan ?? "")} onChange={(e) => updateEmployeeField("jabatan", e.target.value)} placeholder=" Contoh : Staf / C 3" />
                                {/* <select className="form-select" value={employeeForm.jabatan} onChange={(e) => updateEmployeeField("jabatan", e.target.value)}>
                                    <option value="">Pilih jabatan</option>
                                    {["Staff","Staff Senior","Supervisor","Manager","Direktur"].map((v) => <option key={v}>{v}</option> )}
                                </select> */}
                            </FormField>
                            <FormField label="Divisi">
                                <select className="form-select" value={employeeForm.divisi} onChange={(e) => updateEmployeeField("divisi", e.target.value)}>
                                    <option value="">Pilih divisi</option>
                                    {["Teknik","Umum & Keuangan","Teknik","SPI","Lainnya"].map((v) => <option key={v}>{v}</option> )}
                                </select>
                            </FormField>
                            <FormField label="Status Karyawan">
                                <select className="form-select" value={employeeForm.status_pegawai} onChange={(e) => updateEmployeeField("status_pegawai", e.target.value as Employee2["status_pegawai"])}>
                                    <option value="">Pilih status karyawan</option>
                                    <option value="Tetap">Tetap</option>
                                    <option value="Kontrak">Kontrak</option>
                                    <option value="Magang">Magang</option>
                                </select>
                            </FormField>
                            <FormField label="Tanggal Bergabung">
                                <input type="date" className="form-input" value={employeeForm.tgl_bergabung.split('T')[0]} onChange={(e) => updateEmployeeField("tgl_bergabung", e.target.value)} />
                            </FormField>
                            <FormField label="Tanggal Berakhir Kontrak">
                                <input type="date" className="form-input" value={employeeForm.periode_kontrak?.split('T')[0] || ""} onChange={(e) => updateEmployeeField("periode_kontrak", e.target.value || null)} />
                            </FormField>
                            <FormField label="Lokasi Kerja">
                                <input type="text" className="form-input" value={String(employeeForm.lokasi_kerja ?? "")} onChange={(e) => updateEmployeeField("lokasi_kerja", e.target.value)} placeholder=" Contoh : Kantor Pusat, Unit Paguat, Unit Lemito, Unit Taluditi, Unit Randangan, Unit Popayato" />
                                <select className="form-select" value={employeeForm.status_pegawai} onChange={(e) => updateEmployeeField("lokasi_kerja", e.target.value as Employee2["lokasi_kerja"])}>
                                    <option value="">Pilih status karyawan</option>
                                    <option value="Tetap">Tetap</option>
                                    <option value="Kontrak">Kontrak</option>
                                    <option value="Magang">Magang</option>
                                </select>
                            </FormField>
                        </div>
                    )}

                    {activeTab === "rekening" && (
                        <div className="rekening-list">
                            {rekEntries.map(([bankName, accountNumber], index) => (
                                <div key={index} className="json-entry">
                                    <input
                                        className="form-input"
                                        placeholder="Contoh: BCA, BNI, BRI, Mandiri, dll."
                                        value={bankName}
                                        onChange={(e) => updateRek(index, 0, e.target.value)}
                                    />

                                    <input
                                        className="form-input"
                                        placeholder="Contoh: 1234567890"
                                        value={accountNumber}
                                        onChange={(e) => updateRek(index, 1, e.target.value)}
                                    />

                                    <button
                                        type="button"
                                        className="json-rm-btn"
                                        aria-label="Hapus rekening"
                                        onClick={() => removeRek(index)}
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                </div>
                            ))}

                            <button
                                type="button"
                                className="json-add-btn"
                                onClick={addRekEntry}
                            >
                                <Plus size={20} />
                                Tambah Rekening
                            </button>
                        </div>
                    )}

                    {activeTab === "pendidikan" && (
                        <div className="form-grid">
                            <FormField label="Jenjang">
                            <select className="form-select" value={employeeForm.pendidikan.jenjang} onChange={(e) => updatePendidikanField("jenjang", e.target.value)}>
                                {["S3","S2","S1","D4","D3","D2","SMA/SMK"].map((v) => <option key={v}>{v}</option>)}
                            </select>
                            </FormField>
                            <FormField label="Nama Institusi"><input className="form-input" value={employeeForm.pendidikan.institusi} onChange={(e) => updatePendidikanField("institusi", e.target.value)} placeholder="Universitas / Sekolah" /></FormField>
                            <FormField label="Jurusan / Prodi"><input className="form-input" value={employeeForm.pendidikan.jurusan} onChange={(e) => updatePendidikanField("jurusan", e.target.value)} placeholder="Teknik Informatika" /></FormField>
                            <FormField label="Tahun Lulus"><input type="number" className="form-input" value={employeeForm.pendidikan.tahun_lulus ?? ""} onChange={(e) => updatePendidikanField("tahun_lulus", parseInt(e.target.value) || null)} placeholder="2020" /></FormField>
                            <FormField label="Bidang Keahlian" full><input className="form-input" value={employeeForm.bidang_keahlian} onChange={(e) => updateEmployeeField("bidang_keahlian", e.target.value)} placeholder="Backend Development, dll." /></FormField>
                        </div>
                    )}

                    {activeTab === "resign" && (
                        <>
                            <button
                                className="resign-toggle-row"
                                onClick={() => setResignEnabled((p) => !p)}
                            >
                                <input type="checkbox" style={{ accentColor: "var(--accent3)", width: 15, height: 15 }} checked={resignEnabled} readOnly />
                                Aktifkan data resign
                            </button>
                            {resignEnabled ? (
                                <>
                                <div className="form-grid">
                                    <FormField label="Tanggal Keluar"><input type="date" className="form-input" value={employeeForm.resign?.tanggal_keluar ?? ""} onChange={(e) => updateResignField("tanggal_keluar", e.target.value)} /></FormField>
                                    <FormField label="Alasan">
                                    <select className="form-select" value={employeeForm.resign?.alasan ?? ""} onChange={(e) => updateResignField("alasan", e.target.value)}>
                                        {["Pindah domisili","Pekerjaan lain","Keluarga","Kesehatan","Pensiun","Kontrak berakhir","Lainnya"].map((v) => <option key={v}>{v}</option>)}
                                    </select>
                                    </FormField>
                                    <FormField label="Keterangan" full><input className="form-input" value={employeeForm.resign?.keterangan ?? ""} onChange={(e) => updateResignField("keterangan", e.target.value)} placeholder="Catatan tambahan..." /></FormField>
                                </div>
                                </>
                            ) : (
                                <div className="resign-null-msg">
                                Kolom <code style={{ color: "var(--text-dim)" }}>resign</code> akan disimpan sebagai <code style={{ color: "var(--accent2)" }}>null</code>.
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className="modal-footer">
                    {!isFirstTab && (
                        <button className="btn" onClick={handlePreviousTab}>
                            Sebelumnya
                        </button>
                    )}

                    {!isLastTab ? (
                        <button className="btn" onClick={handleNextTab}>
                            Berikutnya
                        </button>
                    ) : !employee ? (
                        <button className="btn" onClick={handleSave}>
                            <Save size={16} className="mr-1" />
                            Simpan
                        </button>
                    ) : (
                        <button className="btn" onClick={handleEdit}>
                            <SquarePen size={16} className="mr-1" />
                            Perbarui
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}